// pages/NlcRequestsPanel.js
// Home panel for "Manager requests, Partner confirms" No Longer Client (server: api/diNlcRequests,
// api/shared/nlcRequests.js).
//
//   * "toConfirm" — pending requests on clients the viewer is Partner of (an unimpersonated admin
//     sees every one): who asked, when, from which section, and the disengagement answers they
//     entered. Confirm marks the client NLC (it then leaves every section); Decline closes the
//     request with an optional note and the client stays. Shown ONE request at a time with Previous /
//     Next. The panel renders only when there is something to decide — independent of whether any
//     section is open for the viewer.
//   * "mine" — the viewer's own pending requests, read-only apart from Withdraw, tucked into a
//     collapsed <details> so it doesn't crowd Home.
//
// Optimistic (repo pattern): the item leaves the list at once, the POST runs, and on failure it is
// restored with the error toast. Refreshes on the app heartbeat (audit field_key 'nlc_request' is
// global, so a request filed or decided elsewhere arrives within ~15 s).

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthContext';
import { useHeartbeat } from '../hooks/useHeartbeat';
import { NLC_REQUESTS_URL } from './sections/nlcRequestUi';
import { postJson } from './sections/reviewUtil';
import {
  DISENGAGEMENT_TYPE_OPTIONS, PROGRAM_OPTIONS, LETTER_DELIVERY_OPTIONS,
} from './sections/disengagementOptions';
import './NlcRequestsPanel.css';

const labelOf = (options, value) => (options.find(o => o.value === value) || {}).label || value;
const byAsked = (a, b) => String(a.requestedAt).localeCompare(String(b.requestedAt));
const when = iso => (iso ? new Date(iso).toLocaleString() : '');

// The disengagement answers as label / value pairs (only what was answered).
function answerRows(d) {
  const rows = [];
  if (d.disengagementType) rows.push(['Terminated by', labelOf(DISENGAGEMENT_TYPE_OPTIONS, d.disengagementType)]);
  if (d.reasonCategory) rows.push(['Reason', d.reasonCategory]);
  if (d.programsToSeparate) {
    rows.push(['Programs to separate', String(d.programsToSeparate).split(',').map(c => labelOf(PROGRAM_OPTIONS, c)).join(', ')]);
  }
  if (d.disengagementLetterNeeded === true) {
    rows.push(['Disengagement letter', `Needed${d.letterDeliveryMethod ? ` — ${labelOf(LETTER_DELIVERY_OPTIONS, d.letterDeliveryMethod)}` : ''}`]);
  } else if (d.disengagementLetterNeeded === false) {
    rows.push(['Disengagement letter', 'Not needed']);
  }
  if (d.lostToFirm) rows.push(['Lost to', d.lostToFirm]);
  if (d.notes) rows.push(['Notes', d.notes]);
  return rows;
}

function RequestCard({ req, sectionLabel, onConfirm, onDecline }) {
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const answers = answerRows(req.disengagement || {});
  return (
    <li className="nlcq-item">
      <div className="nlcq-head">
        <span className="nlcq-client"><code>{req.clientCode}</code> {req.clientName}</span>
        <span className="nlcq-meta">
          Requested by {req.requestedBy}{req.requestedAt ? ` on ${when(req.requestedAt)}` : ''}
          {sectionLabel ? ` · from ${sectionLabel}` : ''}
          {req.relatedTo ? ' · as a related party of another client' : ''}
        </span>
      </div>
      {answers.length > 0 ? (
        <dl className="nlcq-answers">
          {answers.map(([k, v]) => (
            <div key={k} className="nlcq-answer"><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>
      ) : <p className="nlcq-none">No disengagement answers were entered.</p>}
      {declining ? (
        <div className="nlcq-decline">
          <textarea className="nlcq-note" rows={2} maxLength={500} value={note} autoFocus
                    placeholder="Optional note to the requester (why it stays)"
                    onChange={e => setNote(e.target.value)} />
          <div className="nlcq-actions">
            <button type="button" className="nlcq-btn nlcq-btn--danger" onClick={() => onDecline(req, note)}>Decline request</button>
            <button type="button" className="nlcq-btn" onClick={() => { setDeclining(false); setNote(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="nlcq-actions">
          <button type="button" className="nlcq-btn nlcq-btn--primary" onClick={() => onConfirm(req)}
                  title="Mark this client No Longer Client — it leaves every section.">
            Confirm NLC
          </button>
          <button type="button" className="nlcq-btn" onClick={() => setDeclining(true)}
                  title="Keep the client — the request is closed.">
            Decline
          </button>
        </div>
      )}
    </li>
  );
}

export default function NlcRequestsPanel({ sectionLabels = {} }) {
  const { email } = useAuth();
  const [toConfirm, setToConfirm] = useState([]);
  const [mine, setMine] = useState([]);
  // Request ids with a decision in flight: a refresh landing meanwhile must not bring them back.
  const inFlight = useRef(new Set());
  // Which request of `toConfirm` is on screen (one at a time). Clamped on render, so a decided request
  // moves on to the next one, and the last one steps back instead of leaving a blank page.
  const [pageIdx, setPageIdx] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await fetch(NLC_REQUESTS_URL, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      const keep = r => !inFlight.current.has(r.id);
      setToConfirm((j.toConfirm || []).filter(keep));
      setMine((j.mine || []).filter(keep));
    } catch (err) {
      // Home still works without this panel; the next heartbeat / reload retries.
      console.error('nlc requests fetch error', err);
    }
  }, []);

  // Load on mount and whenever the effective identity changes (View As).
  useEffect(() => { setToConfirm([]); setMine([]); load(); }, [load, email]);

  // A request filed / decided by someone else (global heartbeat key), or an overflowed change list.
  useHeartbeat({
    sections: [],
    onChange: ({ changes, truncated }) => {
      if (truncated || changes.some(c => c.global)) load();
    },
  });

  // Optimistic decision: drop the item now, POST, restore it (+ toast) if the server refuses.
  async function decide(req, action, note, okMsg) {
    inFlight.current.add(req.id);
    setToConfirm(list => list.filter(r => r.id !== req.id));
    setMine(list => list.filter(r => r.id !== req.id));
    try {
      await postJson(NLC_REQUESTS_URL, { id: req.id, action, ...(note ? { note } : {}) });
      toast.success(okMsg);
    } catch (err) {
      const restore = list => (list.some(r => r.id === req.id) ? list : [...list, req].sort(byAsked));
      if (action === 'withdraw') setMine(restore); else setToConfirm(restore);
      toast.error(`Couldn't ${action} the request for ${req.clientCode}: ${err.message}`, { autoClose: 10000 });
    } finally {
      inFlight.current.delete(req.id);
      load();     // pick up whatever else changed (e.g. the request was already decided)
    }
  }

  const confirmReq = req => {
    if (!window.confirm(`Mark ${req.clientCode} ${req.clientName || ''} No Longer Client? It will leave every section.`)) return;
    decide(req, 'confirm', null, `${req.clientCode} marked No Longer Client.`);
  };
  const declineReq = (req, note) => decide(req, 'decline', (note || '').trim(), `Request for ${req.clientCode} declined — the client stays.`);
  const withdrawReq = req => decide(req, 'withdraw', null, `Request for ${req.clientCode} withdrawn.`);

  // An admin who is both requester and decider would see a request twice; show it once (to decide).
  const decidable = new Set(toConfirm.map(r => r.id));
  const own = mine.filter(r => !decidable.has(r.id));

  if (!toConfirm.length && !own.length) return null;
  const page = Math.min(pageIdx, Math.max(0, toConfirm.length - 1));
  const current = toConfirm[page];

  return (
    <section className="nlcq-panel" aria-label="No Longer Client requests">
      {toConfirm.length > 0 && (
        <>
          <h2 className="nlcq-title">
            <i className="fas fa-user-slash" /> No Longer Client requests
            <span className="nlcq-count">{toConfirm.length}</span>
          </h2>
          <p className="nlcq-sub">
            A Manager asked for these clients to be marked No Longer Client. Confirming marks the client NLC
            (it leaves every section); declining keeps it.
          </p>
          {toConfirm.length > 1 && (
            <div className="nlcq-pager" role="navigation" aria-label="NLC requests">
              <button type="button" className="nlcq-btn" disabled={page === 0}
                      onClick={() => setPageIdx(page - 1)}>&lsaquo; Previous</button>
              <span className="nlcq-pager-pos" aria-live="polite">Request {page + 1} of {toConfirm.length}</span>
              <button type="button" className="nlcq-btn" disabled={page >= toConfirm.length - 1}
                      onClick={() => setPageIdx(page + 1)}>Next &rsaquo;</button>
            </div>
          )}
          <ul className="nlcq-list">
            <RequestCard key={current.id} req={current} sectionLabel={sectionLabels[current.sectionKey] || ''}
                         onConfirm={confirmReq} onDecline={declineReq} />
          </ul>
        </>
      )}
      {own.length > 0 && (
        <details className="nlcq-mine">
          <summary>Your pending NLC requests ({own.length}) — waiting on the client&rsquo;s Partner</summary>
          <ul className="nlcq-mine-list">
            {own.map(r => (
              <li key={r.id} className="nlcq-mine-item">
                <span><code>{r.clientCode}</code> {r.clientName}
                  <span className="nlcq-meta"> · requested {when(r.requestedAt)}{r.partnerEmail ? ` · Partner: ${r.partnerEmail}` : ''}</span>
                </span>
                <button type="button" className="nlcq-link" onClick={() => withdrawReq(r)}>Withdraw</button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
